import { z } from 'zod';
import {
    SUPPORTED_UNITS,
    SUPPORTED_UNITS_SCHEMA,
    DISPOSAL_PURPOSE_SCHEMA,
    FREIGHT_TYPE_SCHEMA,
    PRODUCT_CATEGORY_SCHEMA,
    PRODUCT_TYPE_SCHEMA,
    PRICING_STRATEGY,
} from '@schuettflix/interfaces';
import { CurrencyCodeSchema } from '@schuettflix/utils';
import { PriceSchema } from '@/shared/schemas/PriceSchema.ts';
import { ProductConfigurationScope } from '@/shared/types/ProductConfigurationScope.ts';

export type CustomProductFormRule = {
    minQuantity?: number;
    unit?: SUPPORTED_UNITS;
    disableDelete?: boolean;
    disableUnit?: boolean;
};

type CustomProductErrorMessages = {
    required?: string;
    tooSmallQuantity?: string;
    francoSalesPriceSignMismatch?: string;
};

function getCustomProductBaseSchema({
    scope,
    errorMessages,
}: {
    scope: ProductConfigurationScope;
    errorMessages?: CustomProductErrorMessages;
}) {
    return z.object({
        scope: z.literal(scope),
        identifier: z.string().optional(),
        productType: PRODUCT_TYPE_SCHEMA,
        category: PRODUCT_CATEGORY_SCHEMA,
        currencyCode: CurrencyCodeSchema,
        unit: z.enum(SUPPORTED_UNITS_SCHEMA.options, {
            required_error: errorMessages?.required,
        }),
        productName: z
            .string({
                message: errorMessages?.required,
            })
            .trim()
            .nonempty({ message: errorMessages?.required }),
        quantity: z.number({
            message: errorMessages?.required,
        }),
        purchasePrice: PriceSchema({
            errorMessages,
        }),
        salesPrice: PriceSchema({ errorMessages }),
        partnerOrganizationId: z.number({
            message: errorMessages?.required,
        }),
        purchaseTaxClassId: z.string({
            message: errorMessages?.required,
        }),
        salesTaxClassId: z.string({
            message: errorMessages?.required,
        }),
        salesforceOpportunityLineItemId: z.string().optional(),
        showZeroPurchasePrice: z.boolean(),
        showZeroSalesPrice: z.boolean(),
        freightType: FREIGHT_TYPE_SCHEMA.optional(),
        disposalPurpose: DISPOSAL_PURPOSE_SCHEMA.optional(),
        serviceDate: z
            .string({
                message: errorMessages?.required,
            })
            .optional(),
    });
}

export function getCustomProductsFormSchema({
    scope,
    errorMessages,
    pricingStrategy,
    formRules,
}: {
    scope: ProductConfigurationScope;
    errorMessages?: CustomProductErrorMessages;
    pricingStrategy: PRICING_STRATEGY;
    formRules?: {
        customProducts?: {
            [identifier: string]: CustomProductFormRule;
        };
    };
}) {
    const customProductSchema = getCustomProductBaseSchema({ scope, errorMessages })
        .and(
            z.discriminatedUnion('productType', [
                z.object({
                    productType: PRODUCT_TYPE_SCHEMA.exclude(['CUSTOM_TRANSPORT']),
                }),
                z.object({
                    productType: PRODUCT_TYPE_SCHEMA.extract(['CUSTOM_TRANSPORT']),
                    freightType: z.enum(FREIGHT_TYPE_SCHEMA.options, {
                        required_error: errorMessages?.required,
                    }),
                }),
            ])
        )
        .and(
            z.discriminatedUnion('freightType', [
                z.object({
                    freightType: FREIGHT_TYPE_SCHEMA.exclude(['HAZARDOUS_WASTE', 'NON_HAZARDOUS_WASTE']).optional(),
                }),
                z.object({
                    freightType: FREIGHT_TYPE_SCHEMA.extract(['HAZARDOUS_WASTE', 'NON_HAZARDOUS_WASTE']),
                    disposalPurpose: z.enum(DISPOSAL_PURPOSE_SCHEMA.options, {
                        required_error: errorMessages?.required,
                    }),
                }),
            ])
        )
        .and(
            z.discriminatedUnion('scope', [
                z.object({
                    scope: z.literal('POSITION'),
                }),
                z.object({
                    scope: z.literal('ORDER'),
                    serviceDate: z.string({
                        message: errorMessages?.required,
                    }),
                }),
            ])
        );

    return z.object({
        customProducts: z
            .array(customProductSchema)
            .superRefine((customProducts, ctx) => {
                customProducts.forEach((product, index) => {
                    const rule =
                        product.identifier !== undefined ? formRules?.customProducts?.[product.identifier] : undefined;

                    if (!rule?.minQuantity || !rule.unit || rule.unit !== product.unit) {
                        return;
                    }

                    if (product.quantity < rule.minQuantity) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.too_small,
                            path: [index, 'quantity'],
                            type: 'number',
                            minimum: rule.minQuantity,
                            inclusive: true,
                            message: errorMessages?.tooSmallQuantity,
                        });
                    }
                });
            })
            .superRefine((customProduct, ctx) => {
                if (pricingStrategy !== 'FRANCO') {
                    return;
                }

                const materialProduct = customProduct[0];
                const transportProduct = customProduct[1];

                if (
                    [Math.sign(materialProduct.salesPrice), 0].every(
                        sign => Math.sign(transportProduct.salesPrice) !== sign
                    )
                ) {
                    [0, 1].forEach(index => {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [index, 'salesPrice'],
                            message: errorMessages?.francoSalesPriceSignMismatch,
                        });
                    });
                }
            }),
    });
}

export type CustomProductBase = z.infer<ReturnType<typeof getCustomProductBaseSchema>>;
export type CustomProductsFormValues = z.infer<ReturnType<typeof getCustomProductsFormSchema>>;
export type CustomProduct = CustomProductsFormValues['customProducts'][number];
